import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Empty, Modal, Popconfirm } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { OW_ASYNC_SELECT_DEFAULT_PAGINATION } from '../ow_async_select/OWAsyncSelect';
import { withUserType } from '../withUserType';
import { ComplianceType } from '../../types/ComplianceTypes';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import ComplianceTypeFormModal from './ComplianceTypeFormModal';

const COMPLIANCE_TYPES_COLLECTION = 'compliance_types';
const COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION = 'complianceTypesIndex';
// The request + document forms feed their compliance-type OWAsyncSelect from this
// shared collection. Refetch it whenever a type is created/edited/deleted here so
// the dropdown reflects the change the next time a form opens.
const TYPES_DROPDOWN_TARGET_COLLECTION = 'complianceTypesDropdown';
const INITIAL_PAGINATION = { current: 1, pageSize: 10 };

interface ComplianceTypesModalProps {
	visible: boolean;
	// Falls back to the logged-in user's role (session.userType, injected by
	// withUserType) when not explicitly provided.
	roleType?: string;
	// Injected by withUserType.
	userType?: string;
	onCancel: () => void;
	totalComplianceTypes: number;
	fetchErrors: string[];
	refetchComplianceTypes: () => void;
	refetchTypesDropdown: () => void;
	deleteComplianceType: (complianceType: ComplianceType) => Promise<any>;
}

const ComplianceTypesModal: FC<ComplianceTypesModalProps> = ({
	visible,
	roleType,
	userType,
	onCancel,
	totalComplianceTypes,
	fetchErrors,
	refetchComplianceTypes,
	refetchTypesDropdown,
	deleteComplianceType,
}): React.ReactElement => {
	const role = roleType || userType;
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [editingComplianceType, setEditingComplianceType] = useState(null);

	const fetchComplianceTypes = useMemo(() => complianceTypesRestCrudThunks(role).read, [role]);

	const openNewTypeModal = () => {
		setEditingComplianceType(null);
		setFormModalOpen(true);
	};

	const closeFormModal = () => setFormModalOpen(false);

	const handleFormSuccess = () => {
		setFormModalOpen(false);
		refetchComplianceTypes();
		refetchTypesDropdown();
	};

	const handleDelete = (complianceType) => {
		deleteComplianceType(complianceType).then(() => {
			refetchComplianceTypes();
			refetchTypesDropdown();
		});
	};

	const renderFlag = (enabled: boolean, enabledLabel: string, disabledLabel: string) => (
		<span className="footnoteregular text-character-secondary">
			{enabled ? (
				<>
					<CheckOutlined className="mr-1" />
					{enabledLabel}
				</>
			) : (
				<>
					<CloseOutlined className="mr-1" />
					{disabledLabel}
				</>
			)}
		</span>
	);

	const renderComplianceType = (_, complianceType: ComplianceType) => (
		<div className="flex flex-col gap-1">
			<div className="h5medium">{complianceType.name}</div>
			<div className="flex flex-row items-center gap-3">
				{renderFlag(complianceType.allowSampleUpload, 'Sample upload', 'No Sample upload')}
				<span className="footnoteregular text-character-secondary">
					{complianceType.requiresUniqueDocument ? 'Unique document' : 'General document'}
				</span>
				<span className="footnoteregular text-character-secondary">
					{complianceType.isLocationSpecific ? 'Location specific' : 'All Locations'}
				</span>
			</div>
		</div>
	);

	const renderComplianceTypeActions = (_, complianceType: ComplianceType) => {
		const handleEditClick = () => {
			setEditingComplianceType(complianceType);
			setFormModalOpen(true);
		};
		const handleDeleteConfirm = () => handleDelete(complianceType);
		return (
			<div className="flex flex-row items-center justify-end gap-2">
				<Button icon={<EditOutlined />} onClick={handleEditClick} />
				<Popconfirm
					title={`Delete "${complianceType.name}"?`}
					okText="Delete"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={handleDeleteConfirm}
				>
					<Button icon={<DeleteOutlined />} />
				</Popconfirm>
			</div>
		);
	};

	const columns = [
		{
			title: 'Compliance type',
			dataIndex: 'name',
			render: renderComplianceType,
		},
		{
			title: '',
			dataIndex: 'id',
			width: 104,
			render: renderComplianceTypeActions,
		},
	];

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="No compliance types yet. Create one to start sending compliance requests."
		/>
	);

	const footer = (
		<div className="flex flex-row items-center justify-between">
			<Button onClick={openNewTypeModal}>+ COMPLIANCE TYPE</Button>
			<Button type="primary" onClick={onCancel}>
				DONE
			</Button>
		</div>
	);

	return (
		<Modal
			visible={visible}
			title={`Compliance Types (${totalComplianceTypes})`}
			onCancel={onCancel}
			footer={footer}
			width={587}
		>
			{fetchErrors.length > 0 && (
				<Alert className="mb-4" type="error" message={fetchErrors.join(', ')} />
			)}
			<PaginatedReduxTable
				showHeader={false}
				// Rows aren't clickable here, so skip the default pointer-cursor row class.
				rowClassName=""
				collectionName={COMPLIANCE_TYPES_COLLECTION}
				targetCollectionName={COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION}
				columns={columns}
				keyAccessor={(complianceType) => complianceType.id}
				initialPagination={INITIAL_PAGINATION}
				fetchData={fetchComplianceTypes}
				emptyState={emptyState}
			/>
			{formModalOpen && (
				<ComplianceTypeFormModal
					visible={formModalOpen}
					roleType={role}
					editingComplianceType={editingComplianceType}
					onCancel={closeFormModal}
					onSuccess={handleFormSuccess}
				/>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	totalComplianceTypes: nullSafeGetOrElse(
		`${COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION}.pagination.total`,
		state.compliance_types,
		0
	),
	fetchErrors: state.compliance_types.fetchErrors,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		// Refetch the page the table is currently showing (PaginatedReduxTable only
		// refetches on its own when filters/sorting/params change, not after a write).
		refetchComplianceTypes: () =>
			dispatch((innerDispatch, getState) => {
				const slice = getState().compliance_types;
				const pagination = nullSafeGetOrElse(
					`${COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION}.pagination`,
					slice,
					INITIAL_PAGINATION
				);
				const sorting = nullSafeGetOrElse(
					`${COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION}.sorting`,
					slice,
					null
				);
				const filters = nullSafeGetOrElse(
					`${COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION}.filters`,
					slice,
					{}
				);
				return innerDispatch(
					complianceTypesRestCrudThunks(role).read(
						{},
						COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION,
						pagination,
						sorting,
						filters
					)
				);
			}),
		// `complianceTypesDropdown` is shared with the OWAsyncSelect pickers in the
		// new-request wizard and the supplier's add-document modal. A bare read here
		// refilled it with the endpoint's default limit of 10, truncating what those
		// pickers had loaded, so refill it the way they do — same page size, same sort.
		refetchTypesDropdown: () =>
			dispatch(
				complianceTypesRestCrudThunks(role).read(
					{},
					TYPES_DROPDOWN_TARGET_COLLECTION,
					OW_ASYNC_SELECT_DEFAULT_PAGINATION,
					{ sort_by: 'name', order: 'ascend' }
				)
			),
		deleteComplianceType: (complianceType) =>
			dispatch(complianceTypesRestCrudThunks(role).deleteImmediate(complianceType, 'id')),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ComplianceTypesModal));
